import React, { useMemo } from 'react';
import { StyleSheet, Pressable, View, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ToolFlagBadges from '@/components/Tools/ToolFlagBadges';
import ToolFavoriteHeart from '@/components/Tools/ToolFavoriteHeart';
import ToolAvatar from '@/components/Tools/ToolAvatar';
import useResponsiveness from '@/hooks/useResponsiveness';
import { formatCount } from '@/utils/agent_catalog';
import { CATEGORY_COLORS, OUTPUT_TYPE_ICON, outputTypeLabel } from '@/components/Tools/ToolCard';

const MIN_USAGE_TO_SHOW = 25;
const isWeb = Platform.OS === 'web';

const createStyles = (colors, isXS, isAltBGColor) => StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: isXS ? 12 : 16,
    paddingVertical: isXS ? 12 : 14,
    paddingHorizontal: isXS ? 14 : 16,
    borderRadius: 14,
    backgroundColor: isAltBGColor ? colors.solid_color_tertiary : colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    width: '100%',
    overflow: 'hidden',
    ...(isWeb && {
      cursor: 'pointer',
      transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
    }),
  },
  rowHovered: {
    ...(isWeb && { boxShadow: '0 10px 24px -18px rgba(0,0,0,0.35)' }),
  },
  avatarFrame: {
    borderRadius: 12,
    overflow: 'hidden',
    flexShrink: 0,
  },
  middle: {
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 2,
  },
  eyebrow: {
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  description: {
    lineHeight: 18,
  },
  trailing: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    gap: 10,
  },
  outputChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 24,
    paddingHorizontal: 9,
    borderRadius: 999,
    backgroundColor: isAltBGColor ? colors.solid_color_secondary : colors.solid_color_tertiary,
  },
  usage: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    minWidth: 64,
    justifyContent: 'flex-end',
  },
  chevron: {
    opacity: 0.55,
  },
  bone: {
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 6,
  },
});

function ToolListItem({ tool, onPress, style: styleProp }) {
  const { colors, theme } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const router = useRouter();
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const styles = useMemo(
    () => createStyles(colors, isXS, isAltBGColor),
    [colors, isXS, isAltBGColor]
  );

  const categorySlug = tool?.category?.slug || (typeof tool?.category === 'string' ? tool.category : null) || 'default';
  const categoryName = tool?.category?.name || null;
  const palette = CATEGORY_COLORS[categorySlug] || CATEGORY_COLORS.default;
  const usage = Number(tool?.stats?.usageCount) || 0;
  const avatarSize = isXS ? 42 : 48;

  const handlePress = () => {
    if (onPress) {
      onPress(tool);
    } else if (tool?.slug) {
      router.push(`/tools/${tool.slug}`);
    }
  };

  return (
    <Pressable
      style={({ hovered }) => [
        styles.row,
        hovered && styles.rowHovered,
        hovered && { borderColor: palette.fg },
        styleProp,
      ]}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={tool?.name}
    >
      <View style={styles.avatarFrame}>
        <ToolAvatar
          tool={tool}
          size={avatarSize}
          iconSize={Math.round(avatarSize * 0.46)}
          borderRadius={12}
          backgroundColor={palette.bg}
          color={palette.fg}
        />
      </View>

      <View style={styles.middle}>
        <View style={styles.titleRow}>
          {categoryName ? (
            <Text size="xxs" weight="bold" style={[styles.eyebrow, { color: palette.fg }]} numberOfLines={1}>
              {categoryName}
            </Text>
          ) : null}
          <ToolFlagBadges tool={tool} omit={['featured', 'new']} />
        </View>
        <Text weight="bold" size={isXS ? 'n' : 'm'} color="primary" numberOfLines={1}>
          {tool?.name}
        </Text>
        <Text size="s" color="secondary" numberOfLines={isXS ? 2 : 1} style={styles.description}>
          {tool?.shortDescription || tool?.description}
        </Text>
      </View>

      <View style={styles.trailing}>
        {!isSM && usage >= MIN_USAGE_TO_SHOW ? (
          <View style={styles.usage}>
            <Icon name="bolt" size={10} color={colors.text_color_tertiary} />
            <Text size="xs" color="tertiary" style={{ fontVariant: ['tabular-nums'] }}>{formatCount(usage)} runs</Text>
          </View>
        ) : null}
        {!isXS && tool?.outputType ? (
          <View style={styles.outputChip}>
            <Icon name={OUTPUT_TYPE_ICON[tool.outputType] || 'circle'} size={11} color={colors.text_color_secondary} />
            <Text size="xs" weight="medium" color="secondary">{outputTypeLabel(tool.outputType)}</Text>
          </View>
        ) : null}
        <ToolFavoriteHeart tool={tool} />
        <View style={styles.chevron}>
          <Icon name="chevron-right" size={13} color={colors.text_color_secondary} />
        </View>
      </View>
    </Pressable>
  );
}

export function ToolListItemPlaceholder() {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const styles = useMemo(
    () => createStyles(colors, isXS, isAltBGColor),
    [colors, isXS, isAltBGColor]
  );
  const avatarSize = isXS ? 42 : 48;

  return (
    <View style={styles.row}>
      <View style={[styles.bone, { width: avatarSize, height: avatarSize, borderRadius: 12, opacity: 0.6 }]} />
      <View style={styles.middle}>
        <View style={[styles.bone, { height: 9, width: '18%', marginBottom: 8, opacity: 0.6 }]} />
        <View style={[styles.bone, { height: 15, width: '42%', marginBottom: 8 }]} />
        <View style={[styles.bone, { height: 12, width: '80%', opacity: 0.7 }]} />
      </View>
    </View>
  );
}

export default React.memo(ToolListItem);
